// @oasis-permission-inherits: /actors (mirage-videos:view)
import { useOnChangeEffect } from "@mirage-web/utils/react-helpers";
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { ActorCard } from "~/modules/mirage/components/actor-card";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";
import { fetchMirageActorsQuery } from "~/modules/mirage/queries";
import { customActorTypeSchema } from "~/modules/mirage/schemas";

export const Route = createFileRoute("/_dash/actors/")({
  validateSearch: z.object({
    id: z.string().optional(),
    type: customActorTypeSchema.optional(),
  }),
  component: RouteComponent,
});

const TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "twin", label: "Twin" },
  { value: "uploaded", label: "Uploaded" },
  { value: "prompt", label: "Prompt" },
  { value: "generated_text_to_image", label: "Text to Image" },
  { value: "generated_image_to_image", label: "Image to Image" },
] as const;

function RouteComponent() {
  const { id, type } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [filterId, setFilterId] = useState<string>(() => id ?? "");

  useOnChangeEffect(id, (v) => {
    setFilterId(v ?? "");
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const id = formData.get("id") as string;
    navigate({
      search: {
        id,
        type,
      },
    });
    setCurrentPageIndex(0);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (pastedText) {
      navigate({
        search: {
          id: pastedText,
          type,
        },
      });
      setCurrentPageIndex(0);
    }
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    fetchMirageActorsQuery.useInfiniteQuery(
      id
        ? {
            params: {
              filters: {
                userId: id,
                ...(type ? { type } : {}),
              },
            },
          }
        : skipToken
    );

  const totalPages = data?.pages.length ?? 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const currentPageData = data?.pages[currentPageIndex];

  const onPreviousPage = () => {
    setCurrentPageIndex((pageIndex) => Math.max(0, pageIndex - 1));
  };
  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => setCurrentPageIndex(pageIndex);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Custom Actors</h1>
      </div>

      <div className="flex w-full h-full justify-between items-center mb-6">
        <form onSubmit={onSubmit} className="flex w-full grow justify-between items-center">
          <fieldset className="flex flex-wrap items-center gap-2">
            <Input
              name="id"
              placeholder="Enter user ID..."
              value={filterId}
              onChange={(e) => {
                setFilterId(e.target.value);
              }}
              onPaste={onPaste}
              className="w-64"
              autoComplete="off"
              autoCorrect="off"
              data-1p-ignore
            />
            <Button type="submit" variant="default" size="sm" className="cursor-pointer">
              Search
            </Button>
          </fieldset>
          <Select
            name="type"
            value={type ?? "all"}
            onValueChange={(v) => {
              setCurrentPageIndex(0);
              navigate({
                search: {
                  id,
                  type: v === "all" ? undefined : customActorTypeSchema.parse(v),
                },
              });
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </form>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      {!id && (
        <div className="flex text-sm text-muted-foreground">
          Enter a user ID to list their custom actors
        </div>
      )}

      {id && (
        <ResponsiveGrid gap={16} maxColumns={5} itemMinWidth={200} itemMinHeight={200}>
          {(isLoading || isFetchingNextPage) &&
            Array.from({ length: 20 }).map((_, index) => (
              <Skeleton key={index} className="aspect-9/16 w-full h-full" />
            ))}
          {currentPageData?.actors.page?.map((actor) => (
            <ActorCard key={actor.id} actor={actor} />
          ))}
          {!isLoading && currentPageData?.actors.page?.length === 0 && (
            <div className="flex text-sm text-muted-foreground">No custom actors found</div>
          )}
        </ResponsiveGrid>
      )}
      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end w-full mb-16">
          <div>
            <InfiniteQueryPagination
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage}
              onPageChange={onPageChange}
              currentPageIndex={currentPageIndex}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}
    </div>
  );
}
